import type { ModuleHealth } from "@/api/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn, relativeAge } from "@/lib/utils";

const MODULE_LABELS: Record<string, string> = {
  ingester: "Ingester",
  correlator: "Correlator",
  soar: "SOAR",
};

const HIGHLIGHTS: Record<string, Array<{ key: string; label: string }>> = {
  ingester: [
    { key: "aroc_ingester_events_published_total", label: "Eventos publicados" },
    { key: "aroc_ingester_polling_cycles_total", label: "Ciclos de polling" },
    { key: "aroc_ingester_errors_total", label: "Errores" },
  ],
  correlator: [
    { key: "aroc_correlator_events_consumed_total", label: "Eventos consumidos" },
    { key: "aroc_correlator_incidents_created_total", label: "Incidentes creados" },
    { key: "aroc_correlator_unmatched_events_total", label: "Eventos sin match" },
  ],
  soar: [
    { key: "aroc_soar_runs_total", label: "Runs totales" },
    { key: "aroc_soar_incidents_auto_resolved_total", label: "Auto-resueltos" },
    { key: "aroc_soar_no_playbook_applicable_total", label: "Sin playbook aplicable" },
  ],
};

export function HealthCard({ module }: { module: ModuleHealth }) {
  const label = MODULE_LABELS[module.name] ?? module.name;
  const highlights = HIGHLIGHTS[module.name] ?? [];
  const indicator = renderIndicator(module.up);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-base">{label}</CardTitle>
        <span
          className={cn("flex items-center gap-2 text-xs font-medium", indicator.className)}
          data-testid={`health-indicator-${module.name}`}
        >
          <span className={cn("size-2.5 rounded-full", indicator.dot)} />
          {indicator.text}
        </span>
      </CardHeader>
      <CardContent className="space-y-3 pt-2 text-sm">
        <div className="text-xs text-muted-foreground">
          {module.last_scrape_at
            ? `Último reporte: ${relativeAge(module.last_scrape_at)}`
            : "Sin datos"}
          {module.error && (
            <div className="mt-1 text-destructive">Error: {module.error}</div>
          )}
        </div>
        <dl className="space-y-1">
          {highlights.map(({ key, label: metricLabel }) => (
            <div key={key} className="flex items-baseline justify-between gap-3">
              <dt className="text-xs text-muted-foreground">{metricLabel}</dt>
              <dd className="font-mono text-sm tabular-nums">
                {formatMetric(module.metrics[key])}
              </dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}

function renderIndicator(up: boolean | null) {
  if (up === true) {
    return { className: "text-green-600", dot: "bg-green-600", text: "UP" };
  }
  if (up === false) {
    return { className: "text-red-600", dot: "bg-red-600", text: "DOWN" };
  }
  return { className: "text-muted-foreground", dot: "bg-muted-foreground", text: "—" };
}

function formatMetric(value: number | undefined): string {
  if (value === undefined) return "—";
  if (Number.isInteger(value)) return value.toLocaleString();
  return value.toFixed(2);
}
